import { useEffect, useRef, useState } from 'react';

export type GmoCardParams = {
  cardno: string; // "0000000000000000"（ハイフンなし）
  expire: string; // "YYMM"
  securitycode: string;
  holdername: string;
};

type UseGmoTokenizationArgs = {
  shopId: string;
  tokenHost: string;
  enabled: boolean; // GMO 新規入力が必要なときのみ true（不要なら token.js を読み込まない）
};

type UseGmoTokenizationResult = {
  ready: boolean;
  loadError: boolean;
  tokenize: (params: GmoCardParams) => Promise<string>;
};

const SCRIPT_ID = 'gmo-multipayment-token-js';

export const useGmoTokenization = ({
  shopId,
  tokenHost,
  enabled
}: UseGmoTokenizationArgs): UseGmoTokenizationResult => {
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !shopId || !tokenHost) return undefined;

    let cancelled = false;

    const init = () => {
      if (cancelled) return;
      if (typeof Multipayment === 'undefined') {
        setLoadError(true);
        return;
      }
      if (!initializedRef.current) {
        Multipayment.init(shopId);
        initializedRef.current = true;
      }
      setReady(true);
    };

    if (typeof Multipayment !== 'undefined') {
      init();
      return undefined;
    }

    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', init, { once: true });
      existing.addEventListener('error', () => !cancelled && setLoadError(true), { once: true });
      return () => {
        cancelled = true;
      };
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `https://${tokenHost}/ext/js/token.js`;
    script.async = true;
    script.addEventListener('load', init, { once: true });
    script.addEventListener('error', () => !cancelled && setLoadError(true), { once: true });
    document.head.appendChild(script);

    return () => {
      cancelled = true;
    };
  }, [enabled, shopId, tokenHost]);

  const tokenize = (params: GmoCardParams): Promise<string> =>
    new Promise((resolve, reject) => {
      if (typeof Multipayment === 'undefined' || !initializedRef.current) {
        reject(new Error('決済モジュールの読み込みが完了していません。少し待って再度お試しください。'));
        return;
      }
      Multipayment.getToken(params, (result: GmoTokenResult) => {
        if (result.resultCode === '000') {
          resolve(result.tokenObject.token);
        } else {
          reject(new Error('カード情報の処理に失敗しました。入力内容をご確認ください。'));
        }
      });
    });

  return { ready, loadError, tokenize };
};
